import { Link } from "react-router-dom"
import {
  FileText, CheckCircle2, UserCheck, KeyRound, ShieldAlert, Copyright,
  AlertTriangle, Lock, RefreshCw, Scale, Mail, ArrowRight,
} from "lucide-react"
import { SiteShell } from "../../components/site/SiteShell"
import { PageHero } from "../../components/site/PageHero"
import { Card, CardContent } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Badge } from "../../components/ui/Badge"
import { Alert } from "../../components/ui/Alert"
import { SCHOOL } from "../../lib/constants"

const clauses = [
  {
    icon: CheckCircle2,
    title: "1. Acceptance of Terms",
    body: [
      `By accessing or using the website, Student Portal, Admin Portal and any related online services of ${SCHOOL.fullName} ("the College", "we", "us", "our"), you agree to be bound by these Terms of Service ("Terms").`,
      "If you do not agree to any part of these Terms, you must not access or use our services. Your continued use of the platform constitutes acceptance of any updated Terms we may publish from time to time.",
      "These Terms form a legally binding agreement between you and the College. If you are accessing the platform on behalf of an institution, you represent that you have the authority to bind that institution to these Terms.",
    ],
  },
  {
    icon: UserCheck,
    title: "2. Eligibility",
    body: [
      "You must be at least sixteen (16) years of age to create an account on the Student Portal. If you are under 18, you confirm that your parent or legal guardian has read and accepted these Terms on your behalf.",
      "Prospective students, enrolled students, alumni, staff, authorised administrators and members of the public may access different parts of the platform. Each user category is subject to additional policies referenced in these Terms.",
      "You must provide accurate, current and complete information during account creation and update such information to keep it accurate. Providing false information may result in immediate account suspension and disqualification from admission.",
    ],
  },
  {
    icon: KeyRound,
    title: "3. User Accounts",
    body: [
      "You are responsible for safeguarding the password and access credentials associated with your account. You agree not to share your password with any third party or permit any unauthorised person to access your account.",
      "You must notify the College immediately of any unauthorised use of your account or any other security breach. The College will not be liable for any loss or damage arising from your failure to comply with this obligation.",
      "The College reserves the right to suspend or terminate accounts that are inactive for more than twelve (12) months, that are found to be in breach of these Terms, or that are associated with fraudulent or malicious activity.",
    ],
  },
  {
    icon: ShieldAlert,
    title: "4. Acceptable Use",
    body: [
      "You agree to use the platform only for lawful purposes and in a manner that does not infringe the rights of, restrict or inhibit anyone else's use of the services. Prohibited conduct includes harassment, defamation, fraud, impersonation and the distribution of malicious software.",
      "You must not attempt to gain unauthorised access to any part of the platform, its server, databases or any other systems or networks connected to the platform. Vulnerability testing or security scanning without prior written authorisation is strictly prohibited.",
      "You must not use automated systems (bots, scrapers, spiders) to extract data from the platform without the College's prior written consent. Bulk downloading of student records, results or certificates for redistribution is prohibited.",
    ],
  },
  {
    icon: Copyright,
    title: "5. Intellectual Property",
    body: [
      "All content on the platform — including text, graphics, logos, images, course materials, examination questions, software and the YMS COE brand — is the property of the College or its licensors and is protected by Nigerian and international intellectual property laws.",
      "Students retain ownership of the academic work they submit for assessment, but grant the College a non-exclusive licence to use, reproduce and adapt such work for educational, accreditation and research purposes without further compensation.",
      "You may not reproduce, distribute, modify, transmit, reuse, download, repost or use any content from the platform for commercial purposes without the prior written consent of the College. Fair use for educational purposes is permitted with proper attribution.",
    ],
  },
  {
    icon: AlertTriangle,
    title: "6. Limitation of Liability",
    body: [
      "The platform and all content are provided on an 'as is' and 'as available' basis. The College does not warrant that the platform will be uninterrupted, error-free, secure or free of harmful components, or that any defects will be corrected.",
      "To the maximum extent permitted by law, the College shall not be liable for any indirect, incidental, special, consequential, punitive or exemplary damages, including but not limited to loss of profits, data, goodwill or academic opportunity, arising from your use of or inability to use the platform.",
      "The College's total liability for any claim arising out of or relating to these Terms shall not exceed the total amount of fees you have paid to the College in the twelve (12) months preceding the event giving rise to the claim.",
    ],
  },
  {
    icon: Lock,
    title: "7. Privacy",
    body: [
      "The College's collection, use and protection of your personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference. Please review the Privacy Policy to understand our data practices.",
      "By using the platform, you consent to the collection, use and processing of your personal information as described in the Privacy Policy. This includes processing for admission, registration, examinations, payments and verification of qualifications.",
      "If you do not agree with the Privacy Policy, you must not use the platform. Continued use of the platform after changes to the Privacy Policy are published constitutes acceptance of those changes.",
    ],
  },
  {
    icon: RefreshCw,
    title: "8. Modifications to Terms",
    body: [
      "The College reserves the right to modify these Terms at any time. We will notify registered users of material changes by email or in-portal announcement at least thirty (30) days before the changes take effect.",
      "Changes that are administrative, clarifying or required by law may take effect immediately upon publication. We encourage you to review these Terms periodically to stay informed of any updates.",
      "Your continued use of the platform after the effective date of any change constitutes your acceptance of the revised Terms. If you do not agree to the modified Terms, you must stop using the platform and notify us in writing.",
    ],
  },
  {
    icon: Scale,
    title: "9. Governing Law",
    body: [
      "These Terms shall be governed by and construed in accordance with the laws of the Federal Republic of Nigeria, without regard to its conflict of law provisions. The College is located in Kano State, Nigeria.",
      "Any dispute arising out of or relating to these Terms or the use of the platform shall first be addressed through good-faith negotiation between the parties. If unresolved within sixty (60) days, the dispute shall be submitted to the jurisdiction of the competent courts of Kano State.",
      "You and the College agree that the United Nations Convention on Contracts for the International Sale of Goods (CISG) shall not apply to these Terms or any transaction conducted through the platform.",
    ],
  },
  {
    icon: Mail,
    title: "10. Contact Information",
    body: [
      `If you have any questions, comments or notices regarding these Terms, please contact the College using the details below. Legal notices should be sent by registered post or email to the address provided.`,
      `Address: ${SCHOOL.address}. Email: legal@ymscoe.edu.ng. Phone: ${SCHOOL.phone}. Office hours: Monday to Friday, 8:00 AM to 5:00 PM West Africa Time (WAT).`,
      "By using the platform, you acknowledge that you have read, understood and agree to be bound by these Terms of Service in their entirety.",
    ],
  },
]

export default function Terms() {
  return (
    <SiteShell>
      <PageHero
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Terms of Service" }]}
        subtitle="Legal"
        title="Terms of Service"
        description="These Terms govern your use of the Yusuf Maitama Sule College of Education website, Student Portal and related online services. Please read them carefully before accessing or using the platform."
      />

      {/* Intro card */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <Card className="border-0 shadow-md bg-gradient-to-br from-brand-50 to-gold-50 dark:from-brand-900/20 dark:to-gold-900/20">
            <CardContent className="pt-6 flex flex-col sm:flex-row items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gold-600 grid place-items-center shrink-0">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100 mb-1">Effective Date: January 1, 2025</h3>
                <p className="text-sm text-ink-600 dark:text-ink-400 leading-relaxed">
                  These Terms constitute a binding agreement between you and Yusuf Maitama Sule College of Education. By using any part of the platform, you confirm that you have read, understood and accepted these Terms.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Clauses */}
      <section className="pb-16">
        <div className="container mx-auto px-4">
          <div className="space-y-6">
            {clauses.map(c => (
              <Card key={c.title} className="border-0 shadow-md">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-700 to-brand-900 grid place-items-center shrink-0 shadow-md">
                      <c.icon className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="font-display font-bold text-xl text-ink-900 dark:text-ink-100 leading-tight pt-2">{c.title}</h2>
                  </div>
                  <div className="space-y-3 pl-0 sm:pl-16">
                    {c.body.map((para, i) => (
                      <p key={i} className="text-sm text-ink-600 dark:text-ink-300 leading-relaxed">{para}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Alert variant="warning" className="mt-8">
            <div className="flex items-start gap-2 text-sm">
              <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
              <div>
                <strong className="block mb-1">Important Notice</strong>
                Violation of any clause in these Terms may result in immediate suspension or termination of your account, disqualification from admission or graduation, and may expose you to civil or criminal liability under Nigerian law. The College reserves all rights not expressly granted in these Terms.
              </div>
            </div>
          </Alert>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-950 dark:bg-ink-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-20" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <Scale className="w-12 h-12 text-gold-300 mx-auto mb-4" />
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">Need Legal Clarification?</h2>
            <p className="text-white/75 mb-6">
              For legal enquiries regarding these Terms, partnership agreements or institutional memoranda of understanding, contact the College Legal Office.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/contact">
                <Button size="lg" className="bg-gold-500 hover:bg-gold-600 text-ink-950">
                  Contact Legal Office <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/privacy">
                <Button size="lg" variant="outline" className="bg-white/5 border-white/30 text-white hover:bg-white/10">
                  View Privacy Policy
                </Button>
              </Link>
            </div>
            <div className="mt-8 text-xs text-white/50">
              <Badge variant="outline" className="border-white/20 text-white/70">Last reviewed: January 2025</Badge>
            </div>
          </div>
        </div>
      </section>
    </SiteShell>
  )
}
